import React, { useCallback, useEffect, useState } from 'react';
import { Grid, GridCol, GridRow } from '@naf/grid';
import { breakpoints, spacing, fontStyle, radius } from '@naf/theme';
import styled from '@emotion/styled';
import { Text, TextVariant } from '@naf/text';
import { AdvancedImage } from '@cloudinary/react';
import { useWindowResize } from '@hooks/Window/useWindowResize';
import { useCloudinary } from '@hooks/Cloudinary/useCloudinary';
import { PortraitsWithTextType, PortraitType } from '@appTypes/portraitsWithTextType';
import BlockContent from '@components/block-content/BlockContent';

type PortraitsWithTextProps = {
  block: PortraitsWithTextType;
};

export const Portrait = ({ item: { name, title, image, text } }: { item: PortraitType }) => {
  const { getResponsiveImage } = useCloudinary();

  // Starting at 500px and setting aspect ratio as portrait.
  const imgWidth = 500;
  const imgHeight = Math.round(500 * 1.25);
  const publicId = image?.public_id || image?.publicId;
  const [cldImage, setCldImage] = useState(
    getResponsiveImage(publicId, { focusMethod: image?.focus, height: imgHeight, width: imgWidth }),
  );

  const advancedImage = cldImage ? (
    <AdvancedImage style={{ maxWidth: '100%' }} alt={image?.alt || image?.altText} cldImg={cldImage} />
  ) : undefined;

  const setResponsiveImage = useCallback(() => {
    if (typeof window !== 'undefined') {
      if (image && image.public_id) {
        if (window.innerWidth <= parseInt(breakpoints.m, 10)) {
          const width = window.innerWidth;
          const height = Math.round(window.innerWidth * 1.25);
          setCldImage(getResponsiveImage(publicId, { focusMethod: image?.focus, height, width }));
        } else {
          // If screen is large, use appr 400 (container) as width
          const width = 400;
          const height = Math.round(400 * 1.25);
          setCldImage(getResponsiveImage(publicId, { focusMethod: image?.focus, height, width }));
        }
      }
    }
  }, [getResponsiveImage, image, publicId]);

  useEffect(() => {
    setResponsiveImage();
  }, [setResponsiveImage]);

  useWindowResize(() => {
    setResponsiveImage();
  });

  return (
    <PortraitWrapper>
      {advancedImage && <ImgWrap>{advancedImage}</ImgWrap>}
      {name && (
        <StyledHeader tag="h3" variant={TextVariant.SubHeader}>
          {name}
        </StyledHeader>
      )}
      {title && (
        <TitleWrapper>
          <StyledTitle tag="p" variant={TextVariant.BodyText}>
            {title}
          </StyledTitle>
        </TitleWrapper>
      )}
      {text && (
        <StyledText className="portraitText">
          <BlockContent value={text} />
        </StyledText>
      )}
    </PortraitWrapper>
  );
};

export const PortraitsWithTextBlock = ({ block }: PortraitsWithTextProps) => {
  if (block.portraits?.length > 0) {
    return (
      <StyledGrid className="PortraitsWithTextBlock">
        <StyledGridRow>
          {block.portraits.map((item) => (
            <GridCol s="12" m="6" l="4" xl="4" key={item._key || item.key}>
              <Portrait item={item} />
            </GridCol>
          ))}
        </StyledGridRow>
      </StyledGrid>
    );
  }
  return null;
};

const StyledGrid = styled(Grid)`
  margin: ${spacing.space48} 0;
`;

const StyledGridRow = styled(GridRow)`
  grid-row-gap: ${spacing.space80} !important;
  @media (max-width: ${breakpoints.m}) {
    grid-row-gap: ${spacing.space56} !important;
  }
`;

const PortraitWrapper = styled.div`
  @media (max-width: ${breakpoints.m}) {
    margin-top: 0;
  }
`;

const StyledHeader = styled(Text)`
  margin-bottom: ${spacing.space8};
`;

const TitleWrapper = styled.div`
  margin-bottom: ${spacing.space12};
`;

const StyledTitle = styled(Text)`
  margin-top: 0;
`;

const ImgWrap = styled.figure`
  margin: 0;
  img {
    border-radius: ${radius.m};
  }
`;

const StyledText = styled.div`
  ${fontStyle.article.caption};

  p {
    margin: 0;
    margin-bottom: ${spacing.space8};

    &:last-of-type {
      margin-bottom: 0;
    }
  }

  ul {
    li {
      ${fontStyle.article.caption};
      list-style: disc;
      padding-left: 0;
      margin-bottom: 0;
      ::before {
        width: 0;
        height: 0;
      }
    }
  }
  @media (max-width: ${breakpoints.m}) {
    margin-top: 0;
  }
`;
